// Inline SVG icon set. Replaces the prior emoji glyphs (⚙🔒✓›💡↶🔇🔈)
// which rendered inconsistently across platforms (colorful on iOS, flat on
// Windows, missing on some Androids) and visually stuttered against the
// Quiet Paper palette. All icons use currentColor + 1.5px stroke on a 20×20
// viewBox so they inherit text color and scale cleanly.
import type { SVGProps } from 'react';

type Props = SVGProps<SVGSVGElement> & { size?: number; strokeWidth?: number };

// PrimeTestLab QA fix #4 (May 2026): default icon size bumped from 20 to
// 24 (px ≈ dp on most Android densities) to meet Android's 24dp minimum
// icon-size guidance. Surrounding tap targets should be wrapped to 48dp
// minimum via padding on the parent button (see styles.css .icon-btn).
// Icons that are passed an explicit size prop (e.g., the "?" hint icon
// in the header) override this default and are unaffected.
function IconBase({ size = 24, strokeWidth = 1.5, children, ...rest }: Props & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function SettingsIcon(p: Props) {
  // Proper gear (cog): outer ring + 8 protruding teeth + central bolt hole.
  // The previous version (8 short radial lines around a disk) read as a
  // SUN icon to testers, not a gear — flagged in the May 2026 PrimeTestLab
  // QA report. This redesign uses three layered elements:
  //   1. Outer ring (the cog body)
  //   2. 8 small protruding nubs around the ring (the gear teeth)
  //   3. Inner small ring (the bolt hole / axle)
  // Together these form the unambiguous gear silhouette that matches
  // Material Symbols and Heroicons settings conventions.
  return (
    <IconBase {...p}>
      {/* Outer ring — the cog body */}
      <circle cx="10" cy="10" r="5" />
      {/* 8 teeth as small protruding nubs at the cardinal + diagonal points */}
      <path d="M 10 2 L 10 4 M 10 16 L 10 18 M 2 10 L 4 10 M 16 10 L 18 10 M 4.34 4.34 L 5.76 5.76 M 14.24 14.24 L 15.66 15.66 M 4.34 15.66 L 5.76 14.24 M 14.24 5.76 L 15.66 4.34" />
      {/* Center bolt hole — small inner circle */}
      <circle cx="10" cy="10" r="1.5" />
    </IconBase>
  );
}

export function HintIcon(p: Props) {
  // "An idea" — a small filled dot inside a circle. Not a lightbulb.
  return (
    <IconBase {...p}>
      <circle cx="10" cy="10" r="6" />
      <circle cx="10" cy="10" r="1.4" fill="currentColor" stroke="none" />
    </IconBase>
  );
}

export function UndoIcon(p: Props) {
  // Curved arrow sweeping counter-clockwise from upper-left.
  return (
    <IconBase {...p}>
      <path d="M5 8 H12.5 A3.5 3.5 0 0 1 12.5 15 H7.5" />
      <path d="M8 5 L5 8 L8 11" />
    </IconBase>
  );
}

export function CloseIcon(p: Props) {
  return (
    <IconBase {...p}>
      <path d="M5 5 L15 15 M15 5 L5 15" />
    </IconBase>
  );
}

export function MuteIcon(p: Props) {
  return (
    <IconBase {...p}>
      <path d="M4 8 V12 H7 L11 15 V5 L7 8 Z" />
      <path d="M14 8 L18 12 M18 8 L14 12" />
    </IconBase>
  );
}

export function UnmuteIcon(p: Props) {
  return (
    <IconBase {...p}>
      <path d="M4 8 V12 H7 L11 15 V5 L7 8 Z" />
      <path d="M14 7.5 A4 4 0 0 1 14 12.5" />
      <path d="M16 5 A7 7 0 0 1 16 15" />
    </IconBase>
  );
}

export function LockIcon(p: Props) {
  return (
    <IconBase {...p}>
      <rect x="4.5" y="9" width="11" height="8" rx="1.5" />
      <path d="M7 9 V6 A3 3 0 0 1 13 6 V9" />
    </IconBase>
  );
}

export function CheckIcon(p: Props) {
  return (
    <IconBase {...p}>
      <path d="M4 10.5 L8 14.5 L16 6" />
    </IconBase>
  );
}

export function ChevronRightIcon(p: Props) {
  return (
    <IconBase {...p}>
      <path d="M8 5 L13 10 L8 15" />
    </IconBase>
  );
}

export function BackIcon(p: Props) {
  return (
    <IconBase {...p}>
      <path d="M12 5 L7 10 L12 15" />
    </IconBase>
  );
}

export function HelpIcon(p: Props) {
  // Question-mark in a circle. Used by the per-screen tutorial-recall
  // button (v0.12.1+) — players who didn't absorb the mechanic on first
  // play tap this to re-open the world's mechanic tutorial (or the base
  // "How to play" for W1-W10). Quiet Paper voice: no exclamation, just
  // a calm "?" against the muted-ink stroke.
  return (
    <IconBase {...p}>
      <circle cx="10" cy="10" r="7.5" />
      {/* The "?" stem: small curl up top, vertical drop to a slight gap above the dot */}
      <path d="M7.5 8 A2.5 2.5 0 0 1 12.5 8 C12.5 9.4 11.2 10 10 10.6 V12" />
      {/* Dot below */}
      <circle cx="10" cy="14.5" r="0.9" fill="currentColor" stroke="none" />
    </IconBase>
  );
}
